import { CSSProperties, useEffect, useState } from "react";
import "styles/rc-slider.css";
import { pageView, track } from "analytics";
import Loader from "elements/Loader";
import { CONVERSIONS_VIDEOS } from "modules/Types";
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import useUser, { UserExistsState } from 'Hooks/useUser';
import useAppView from 'Hooks/useAppView';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import { useCampaignModal } from 'Hooks/useGlobalModals';
import { useViewOnlyAccess, ViewOnlyFeature } from 'Hooks/useViewOnlyAccess';
import PageSymphony from "elements/PageSymphony";
import DefaultLearningIndicator from "components/shareable/LearningIndicator/DefaultLearningIndicator";
import CardSymphony from "elements/CardSymphony";
import TabSymphony from "elements/TabSymphony";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import WarningNoReleaseUpc from "./PreSave/Components/WarningNoReleaseUpc/WarningNoReleaseUpc";
import { LearningIndicatorType } from "components/shareable/LearningIndicator/DefaultLearningIndicator";
import DefaultAdditionalActions from "components/shareable/DefaultAdditionalActions";
import { deepCopy, makeKey } from "modules/Utils";
import ButtonSymphony from "elements/ButtonSymphony";
import DropdownArrow from "components/shareable/DropdownArrow";
import useScreen from "Hooks/useScreen";
import CampaignFilterOptions from "./Components/CampaignTypesFilter/CampaignFilterOptions";
import { useCampaign } from "Hooks/useCampaign";
import CampaignInfoSymphony from "elements/CampaignInfoSymphony";
import { useHistory } from "react-router-dom";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import useMaestroInstagramPosts from "Hooks/useMaestroInstagramPosts";
import { CAMPAIGN_FILTER_LIMIT_DEFAULT } from "./constants";
import Colors from "modules/Colors";
import { PROMOTABLE_ITEMS } from "modules/Campaign";
import { AdPlatform, CampaignStatus, CampaignType, PLAYBOOK_TYPE_INFO, PlaybookType } from "modules/Const";
import { OrganizationCampaignData } from "api/organizationApi";
import { CampaignDetails } from "modules/Types";

import Banner from "elements/Banner";
import ConnectionsTab from "pages/SettingsPage/Components/ConnectionsTab";
import CreateNewCampaignModalV2 from "components/CreateNewCampaignModalV2";

enum MarketingPageTabs {
    CAMPAIGNS = 'campaigns',
    CAMPAIGN_DRAFTS = 'campaignDrafts',
    CONNECTIONS = 'connections',
    TOOLS = 'tools',
    INSTAGRAM = 'instagram',
}

const MARKETING_TABS = [
    {
        id: MarketingPageTabs.CAMPAIGNS,
        title: 'Campaigns',
    },
    {
        id: MarketingPageTabs.CAMPAIGN_DRAFTS,
        title: 'Drafts',
    },
    {
        id: MarketingPageTabs.CONNECTIONS,
        title: 'Connections',
    },
];

const MarketingPage = () => {
    const history = useHistory()
    const { mobileView } = useScreen();
    const {
        setIsShowMaestroInstagramInputModal
    } = useMaestroInstagramPosts()

    const { isTeamView, isBrandView } = useAppView();
    const { organization } = useOrganizationV2();

    // Brand view hooks
    const brandState = useBrand();
    const currentBrand = brandState?.currentBrand;
    const isBrandAdmin = brandState?.isBrandAdmin || false;
    const getIsPaymentFailed = brandState?.getIsPaymentFailed;

    const { isSymphonyBetaTestUser } = useUser() as UserExistsState;
    
    // View Only access control
    const { hasAccess: canCreateCampaigns } = useViewOnlyAccess(ViewOnlyFeature.CAMPAIGN_CREATE);

    const {
        setShowCreateNewCampaignModal
    } = useCampaignBuilder()

    const { openCampaignModal } = useCampaignModal()

    const {
        isLoadingCampaigns,
        campaigns,
        getCampaigns,
        campaignDrafts,
        getCampaignDrafts,
        resetMarketingCampaigns,
        isCampaignsLimitReached,
        isCampaignDraftsLimitReached,
        setIsCampaignsLimitReached,
        setIsCampaignDraftsLimitReached,
        campaignFilter,
        setCampaignFilter,
        campaignDraftFilter,
        setCampaignDraftFilter,
        isCampaignFilter,
        isCampaignsInitialized,
        // Organization methods - only needed for team view
        getOrganizationCampaigns,
        getOrganizationCampaignDrafts,
    } = useCampaign()

    const isEmptyCampaigns = campaigns.length === 0
    

    // Brand-specific features (only show in brand view)
    const showPaymentFailedBanner = !isTeamView() && currentBrand && getIsPaymentFailed ? getIsPaymentFailed() : false

    const [selectedMarketingTab, setSelectedMarketingTab] = useState<TabItem>(MARKETING_TABS[0]);

    const [isShowFilterItems, setIsShowFilterItems] = useState<boolean>(false);

    /** Dynamically filter tabs based on view mode and permissions */
    const visibleMarketingTabs = MARKETING_TABS.filter(tab => {
        // In team view, show campaigns and drafts, but not connections (brand-specific)
        if (isTeamView()) {
            if (tab.id === MarketingPageTabs.CONNECTIONS) {
                return false; // Connections are brand-specific
            }
            if (tab.id === MarketingPageTabs.CAMPAIGN_DRAFTS) {
                return campaignDrafts.length > 0;
            }
            return true;
        }

        // In brand view, check for view-only restrictions
        if (isBrandView()) {
            // View-only users can only see the Campaigns tab
            if (!canCreateCampaigns) {
                return tab.id === MarketingPageTabs.CAMPAIGNS;
            }
            
            // Full access users see drafts only if they exist
            if (tab.id === MarketingPageTabs.CAMPAIGN_DRAFTS) {
                return campaignDrafts.length > 0;
            }
            return true;
        }
        
        return true;
    });

    useEffect(() => {
        pageView("Marketing");

        return () => {
            resetMarketingCampaigns()
        }
    }, []);

    useEffect(() => {
        if (isTeamView() && organization?.id) {
            // Team view: campaigns are already loaded during organization initialization
            // Only load if not already initialized to avoid unnecessary calls
            if (!isCampaignsInitialized) {
                getOrganizationCampaigns({ organizationId: organization.id })
                getOrganizationCampaignDrafts({ organizationId: organization.id })
            }
        } else if (isBrandView() && currentBrand?.id) {
            // Brand view: fetch brand campaigns using getCampaigns
            getCampaigns({ brandId: currentBrand.id })
            getCampaignDrafts({ brandId: currentBrand.id })
        }
    }, [isTeamView, isBrandView, organization?.id, currentBrand?.id, isCampaignsInitialized]);

    const renderFilterButton = () => {
        return (
            <ButtonSymphony
                className="filtersMarketingToggleButton"
                variant="outlined"
                onClick={() => setIsShowFilterItems(!isShowFilterItems)}
                style={{
                    paddingLeft: 18,
                    paddingRight: 14,
                }}
                iconRight={
                    <DropdownArrow
                        color={Colors.purple}
                        width={16}
                        height={16}
                        direction={isShowFilterItems ? "up" : "down"}
                    />
                }
            >
                Filters
            </ButtonSymphony>
        )
    }

    const renderAllCampaignsLoadedMessage = () => {
        if (isLoadingCampaigns || !campaigns.length || !isCampaignsInitialized) {
            return null
        }

        return (
            <div
                className="allCampaignDraftsLoadedMessage"
                style={{
                    textAlign: 'center',
                    paddingTop: 24,
                    paddingBottom: 24,
                    color: Colors.greyDark,
                }}
            >
                All campaigns loaded
            </div>
        )
    }

    const renderZeroState = () => {
        // Don't show anything while loading or if campaigns aren't initialized
        if (isLoadingCampaigns || !isCampaignsInitialized) {
            return null;
        }

        // Show "View Only" banner for users without campaign creation access in brand view
        if (!isTeamView() && !canCreateCampaigns && currentBrand) {
            return (
                <Banner
                    className="marketingPageTabCampaignsNoCampaignsBanner"
                    title={`You have "View Only" access to ${currentBrand.name}`}
                    description="Ask your team admin to add you as a full admin to this brand in order to create campaigns."
                    color="yellow"
                />
            );
        }

        // Show "No campaigns found" message when filters are applied but no results
        if (isEmptyCampaigns && isCampaignFilter) {
            return (
                <div className="noCampaignsFoundMessage" style={sty.noCampaignsFoundMessage}>
                    No campaigns found
                </div>
            );
        }

        // Show campaign creation zero state when no campaigns exist and no filters applied
        const shouldShowCreationZeroState = isEmptyCampaigns && !isCampaignFilter && (
            // Team view: always show zero state when empty
            isTeamView() || 
            // Brand view: only show for users who can create campaigns
            (!isTeamView() && canCreateCampaigns)
        );

        if (shouldShowCreationZeroState) {
            return (
                <CreateNewCampaignModalV2
                    className="marketingPageTabCampaignsCreateNewCampaignModal"
                    isModal={false}
                    height={467}
                    isBorder
                />
            );
        }

        return null;
    }

    const renderCampaignsTab = () => {
        return (
            <TabSymphony
                className="marketingPageTabCampaigns"
                visible={selectedMarketingTab.id === MarketingPageTabs.CAMPAIGNS}
            >
                {/* <DebugBox
                    data={{
                        currentBrand,
                    }}
                    alwaysShow
                /> */}
                {renderZeroState()}
                {campaigns.filter(campaign => campaign.status !== CampaignStatus.DRAFT).map((campaign: CampaignDetails | OrganizationCampaignData, index: number) => {
                    return (
                        <CampaignInfoSymphony
                            key={makeKey(campaign, index)}
                            className="campaignCardSymphonyMarketingPage"
                            campaign={campaign}
                            onClick={() => history.push(`/marketing/campaigns/${campaign.id}`)}
                            onClickExternal={() => window.open(`/marketing/campaigns/${campaign.id}`, '_blank')}
                            brandInfo={'brandInfo' in campaign ? campaign.brandInfo : undefined}
                        />
                    )
                })}
                {isCampaignsInitialized && !isCampaignsLimitReached && campaigns.length > 3 ?
                    <ButtonSymphony
                        className="loadMoreCampaignsButton"
                        style={sty.loadMoreCampaignsButton}
                        variant="text"
                        onClick={async () => {
                            const originalCampaignsLength = campaigns.length
                            const campaignsLimitLocal = campaignFilter.limit + CAMPAIGN_FILTER_LIMIT_DEFAULT
                            const campaignFilterLocal = deepCopy(campaignFilter)
                            campaignFilterLocal.limit = campaignsLimitLocal
                            setCampaignFilter(campaignFilterLocal)

                            let newCampaigns = [];
                            if (isTeamView() && organization?.id) {
                                newCampaigns = await getOrganizationCampaigns({
                                    organizationId: organization.id,
                                    params: campaignFilterLocal
                                }) || []
                            } else if (isBrandView() && currentBrand?.id) {
                                newCampaigns = await getCampaigns({
                                    brandId: currentBrand.id,
                                    params: campaignFilterLocal
                                }) || []
                            }

                            if (originalCampaignsLength === newCampaigns.length) {
                                setIsCampaignsLimitReached(true)
                            }
                        }}
                        isLoading={isLoadingCampaigns}
                        disabled={isLoadingCampaigns}
                        loadingText="Loading Campaigns..."
                    >
                        Load More Campaigns
                    </ButtonSymphony>
                    :
                    renderAllCampaignsLoadedMessage()
                }
                {/* <CampaignsSection
                    addedFilters={filters !== DEFAULT_CAMPAIGN_PARAMS}
                    locallyLoading={isLoadingCampaigns}
                    campaigns={campaigns}
                    // campaignsInDraft={campaignsInDraft}
                    // onDraftDelete={onDraftDelete}
                    fullyReturned={hasMoreCampaigns}
                    loadingMore={loadingMore}
                    loadNextPage={() => {
                        if (hasMoreCampaigns) {
                            loadMoreCampaigns()
                        }
                    }}
                /> */}
            </TabSymphony>
        )
    }

    const renderCampaignDraftsTab = () => {
        return (
            <TabSymphony
                className="marketingPageTabCampaignDrafts"
                visible={selectedMarketingTab.id === MarketingPageTabs.CAMPAIGN_DRAFTS}
            >
                {campaignDrafts.length === 0 && !isLoadingCampaigns && isCampaignsInitialized &&
                    <div className="noCampaignDraftsMessage" style={sty.noCampaignsFoundMessage}>
                        No campaign drafts available!
                    </div>
                }
                {campaignDrafts.map((campaign: CampaignDetails | OrganizationCampaignData, index: number) => {
                    if (campaign.campaign_metadata?.campaign_type === CampaignType.PRE_SAVE) {
                        return null
                    }

                    return (
                        <CampaignInfoSymphony
                            key={makeKey(campaign, index)}
                            className="campaignCardSymphonyDraftMarketingPage"
                            campaign={campaign}
                            onClick={() => {
                                const playbookType = campaign.campaign_metadata?.playbook || campaign.campaign_metadata?.campaign_type
                                const adPlatform = PLAYBOOK_TYPE_INFO[playbookType as PlaybookType].adPlatform
                                switch (adPlatform) {
                                    case AdPlatform.YOUTUBE:
                                        history.push(`/marketing/new/video-views?draft=${campaign.id}`)
                                        break
                                    case AdPlatform.PLAYLISTS:
                                        history.push(`/marketing/new/playlist-followers?draft=${campaign.id}`)
                                        break
                                    default:
                                        history.push(`/marketing2/draft/${campaign.id}`)
                                        break
                                }
                            }}
                            brandInfo={'brandInfo' in campaign ? campaign.brandInfo : undefined}
                        />
                    )
                })}
                {isCampaignsInitialized && !isCampaignDraftsLimitReached && campaignDrafts.length > 3 ?
                    <ButtonSymphony
                        className="loadMoreCampaignsButton"
                        style={sty.loadMoreCampaignsButton}
                        variant="text"
                        onClick={async () => {
                            const originalCampaignDraftsLength = campaignDrafts.length
                            const campaignsDraftsLimitLocal = campaignDraftFilter.limit + CAMPAIGN_FILTER_LIMIT_DEFAULT
                            const campaignDraftFilterLocal = deepCopy(campaignDraftFilter)
                            campaignDraftFilterLocal.limit = campaignsDraftsLimitLocal
                            setCampaignDraftFilter(campaignDraftFilterLocal)
                            
                            let newCampaignDrafts = [];
                            if (isTeamView() && organization?.id) {
                                newCampaignDrafts = await getOrganizationCampaignDrafts({
                                    organizationId: organization.id,
                                    params: campaignDraftFilterLocal
                                }) || []
                            } else if (isBrandView() && currentBrand?.id) {
                                newCampaignDrafts = await getCampaignDrafts({
                                    brandId: currentBrand.id,
                                    params: campaignDraftFilterLocal
                                }) || []
                            }

                            if (originalCampaignDraftsLength === newCampaignDrafts.length) {
                                setIsCampaignDraftsLimitReached(true)
                            }
                        }}
                        isLoading={isLoadingCampaigns}
                        disabled={isLoadingCampaigns}
                        loadingText="Loading More Campaign Drafts..."
                    >
                        Load More Campaign Drafts
                    </ButtonSymphony>
                    :
                    (campaignDrafts.length > 0 ? renderAllCampaignsLoadedMessage() : null)
                }
            </TabSymphony>
        )
    }

    const renderConnectionsTab = () => {
        return (
            <TabSymphony
                className="marketingPageTabConnections"
                visible={selectedMarketingTab.id === MarketingPageTabs.CONNECTIONS}
            >
                <ConnectionsTab className="marketingPageConnectionsTab" />
            </TabSymphony>
        )
    }

    const renderFilters = () => {
        if (isShowFilterItems) {
            return (
                <CampaignFilterOptions />
            )
        }
    }

    const renderAdditionalActionButtons = () => {
        // Show campaign creation buttons in both team view and brand view with campaign creation access
        if (!isTeamView() && (!canCreateCampaigns || !currentBrand)) {
            return undefined;
        }

        const campaignItems = PROMOTABLE_ITEMS
            .filter(promotableItem => promotableItem.isPopular && (!promotableItem.onlyForSymphonyBetaTestUsers || isSymphonyBetaTestUser))
            .map(promotableItem => ({
                key: promotableItem.playbookType,
                title: promotableItem.title,
                icon: promotableItem.icon,
                link: promotableItem.link
            }))

        return (
            <DefaultAdditionalActions
                className="defaultAdditionalActionsGoalSelector"
                buttonText="Start New Campaign"
                additionalItemsText="Popular Campaigns:"
                onButtonClick={() => {
                    // NOTE: this is for start new campaign button click
                    if (isTeamView()) {
                        // In team view, use global modal system which will show brand selector first
                        openCampaignModal({
                            source: 'Start New Campaign Button - Team View'
                        })
                        track("Campaign Started", {
                            from: 'Start New Campaign Button - Team View',
                        });
                    } else {
                        // In brand view, proceed normally with existing system
                        openCampaignModal({
                            source: 'Start New Campaign Button - Brand View'
                        })
                        track("Campaign Started", {
                            brand_id: currentBrand?.id,
                            brand_name: currentBrand?.name,
                            brand_slug: currentBrand?.slug,
                            from: 'Start New Campaign Button',
                        });
                    }
                }}
                campaignItems={campaignItems.slice(0, 3)}
                onCampaignItemClick={(campaignType: any) => {
                    // NOTE: this is for popular campaign quick action clicks
                    if (isTeamView()) {
                        // In team view, use global modal system which will show brand selector first
                        openCampaignModal({
                            source: 'popular - Team View',
                            adPlatform: PLAYBOOK_TYPE_INFO[campaignType as PlaybookType]?.adPlatform
                        })
                        const commonTrackData = {
                            playbookType: campaignType,
                            adPlatform: PLAYBOOK_TYPE_INFO[campaignType as PlaybookType]?.adPlatform,
                            from: 'popular - Team View',
                        }
                        track("Campaign Started", {
                            ...commonTrackData,
                        });
                        track("Playbook Selection", {
                            ...commonTrackData,
                        });
                    } else {
                        // In brand view, proceed normally with existing system
                        setShowCreateNewCampaignModal(true)
                        const commonTrackData = {
                            playbookType: campaignType,
                            adPlatform: PLAYBOOK_TYPE_INFO[campaignType as PlaybookType]?.adPlatform,
                            brand_id: currentBrand?.id,
                            brand_name: currentBrand?.name,
                            brand_slug: currentBrand?.slug,
                            from: 'popular',
                        }
                        track("Campaign Started", {
                            ...commonTrackData,
                        });
                        track("Playbook Selection", {
                            ...commonTrackData,
                        });
                    }
                }}
            />
        )
    }

    // NOTE: render()
    return (
        <>
            <PageSymphony
                className="goalSelectorComponent"
                isContent
            >
                {/* <DebugBox
                    data={{
                        isLoadingCampaigns,
                        isCampaignsInitialized,
                        isCampaignsLimitReached,
                        isCampaignDraftsLimitReached,
                        isCampaignFilter,
                        isShowFilters,
                        isShowFilterItems,
                        campaignFilter,
                        campaigns,
                        // campaignDrafts,
                    }}
                    alwaysShow
                /> */}
    
                <CardSymphony
                    className="cardTitleGoalSelector"
                    title={"Marketing"}
                    subtitle={
                        "Grow your fanbase with AI-powered marketing."
                    }
                    additionalButtons={renderAdditionalActionButtons()}
                >
                    <DefaultLearningIndicator
                        text="Learn how to use Symphony's Marketing tools"
                        type={LearningIndicatorType.MARKETING}
                    />
                    {/* <ButtonSymphony
                        className="maestroInstagramAnalyzerButton"
                        variant="outlined"
                        onClick={() => setIsShowMaestroInstagramInputModal(true)}
                    >
                        Maestro Instagram Analyzer
                    </ButtonSymphony> */}
                </CardSymphony>


                <WarningNoReleaseUpc campaigns={campaigns} />
                <CardSymphony
                    className="marketingPageTabsContainer"
                >
                    <div
                        className="marketingPageTabsNavBar"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 16,
                        }}
                    >
                        <TabsSymphony
                            className='symphonyTabsGoalSelector'
                            selectedTab={selectedMarketingTab}
                            onChange={(tab) => setSelectedMarketingTab(tab as TabItem)}
                            tabs={visibleMarketingTabs}
                            isBorderBottom
                            isLoading={isLoadingCampaigns}
                            rightSideContent={renderFilterButton()}
                        />
                    </div>
                    {renderFilters()}
                    {((!isCampaignsInitialized && (isEmptyCampaigns || isLoadingCampaigns)) || (isEmptyCampaigns && isLoadingCampaigns)) &&
                        <Loader
                            className="marketingPageLoader"
                            title="Loading Campaigns..."
                            isBackground
                        />
                    }
                    {renderCampaignsTab()}
                    {renderCampaignDraftsTab()}
                    {renderConnectionsTab()}
                </CardSymphony>
            </PageSymphony >
        </>
    );
}

const sty: Record<string, CSSProperties> = {
    noCampaignsBanner: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        gap: 16,
    },
    loadMoreCampaignsButton: {
        marginTop: 24,
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    noCampaignsFoundMessage: {
        textAlign: 'center',
        paddingTop: 48,
        paddingBottom: 48,
        paddingLeft: 16,
        paddingRight: 16,
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
    }
}

export default MarketingPage;